import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import CelebrationIcon from '@mui/icons-material/Celebration';

interface CountdownTimerProps {
  startTime: Date;
  endTime: Date;
  clearMode?: boolean;
}

const formatTime = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return {
    hours: hours.toString().padStart(2, '0'),
    minutes: minutes.toString().padStart(2, '0'),
    seconds: secs.toString().padStart(2, '0'),
  };
};

const CountdownTimer: React.FC<CountdownTimerProps> = ({ startTime, endTime, clearMode }) => {
  // Hooksは必ず最上部で呼ぶ
  const [now, setNow] = useState<Date>(new Date());
  const [isSecondEven, setIsSecondEven] = useState<boolean>(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
      setIsSecondEven(true);
    }, 1000);
    return () => clearInterval(timer);
  }, [startTime, endTime]);

  useEffect(() => {
    const blinkTimer = setInterval(() => {
      setTimeout(() => {
        setIsSecondEven(false);
      }, 500);
    }, 1000);
    return () => clearInterval(blinkTimer);
  }, []);

  if (clearMode) {
    return (
      <Box 
        sx={{ 
          p: 3, 
          background: 'linear-gradient(135deg, #e3f2fd 0%, #fff1f8 100%)',
          borderRadius: 3,
          border: '2.5px solid #43a047',
          boxShadow: '0 0 24px #81c784',
          textAlign: 'center',
        }}
      >
        <Typography variant="h3" sx={{ fontWeight: 700, color: '#43a047', mb: 2, letterSpacing: 2, textShadow: '0 2px 8px #fff' }}>
          🎉 イベントクリア！ 🎉
        </Typography>
        
        {/* 特典画像 */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mb: 4,
            animation: 'bounce 2s infinite',
            '@keyframes bounce': {
              '0%, 20%, 50%, 80%, 100%': { transform: 'translateY(0)' },
              '40%': { transform: 'translateY(-10px)' },
              '60%': { transform: 'translateY(-5px)' },
            }
          }}
        >
          <img 
            src="/image/clear.png" 
            alt="クリア特典画像 - 右クリック（長押し）で保存できます" 
            title="クリア特典画像 - 右クリック（長押し）で保存できます"
            style={{
              maxWidth: '350px',
              maxHeight: '350px',
              width: 'auto',
              height: 'auto',
              boxShadow: '0 8px 32px rgba(67, 160, 71, 0.4)',
              borderRadius: '12px',
              border: '3px solid #43a047',
              cursor: 'pointer',
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(67, 160, 71, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(67, 160, 71, 0.4)';
            }}
            onContextMenu={(e) => {
              // コンテキストメニューを許可（右クリック保存のため）
              e.stopPropagation();
            }}
          />
        </Box>
        
        <Typography variant="body2" sx={{ 
          color: '#2e7d32', 
          fontWeight: 500, 
          mb: 2,
          fontStyle: 'italic',
          opacity: 0.8
        }}>
          保存して見てね！
        </Typography>
        
        <Typography variant="h6" sx={{ color: '#388e3c', fontWeight: 600, mb: 2 }}>
          よく頑張ったね！
        </Typography>

        
        <Chip 
          label="Congratulations!"
          color="success"
          sx={{ 
            background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            color: 'white',
            fontWeight: 'bold',
            fontSize: 23,
            boxShadow: '0 0 10px #b2ff59',
            border: '1.5px solid #43a047',
            letterSpacing: 1,
            mt: 2
          }}
        />
      </Box>
    );
  }

  const getSecondsUntil = (target: Date) => {
    const now = new Date();
    return Math.max(0, Math.floor((target.getTime() - now.getTime()) / 1000));
  };

  if (now < startTime) {
    // 開始前ワクワク画面
    const secondsToStart = getSecondsUntil(startTime);
    const timeObj = formatTime(secondsToStart);
    return (
      <Box 
        sx={{ 
          p: 3, 
          background: 'linear-gradient(135deg, #e3f2fd 0%, #fff1f8 100%)',
          borderRadius: 3,
          border: '2.5px solid #1976d2',
          boxShadow: '0 0 24px #90caf9',
          textAlign: 'center',
          animation: 'pulseStart 1.5s infinite',
          '@keyframes pulseStart': {
            '0%': { boxShadow: '0 0 24px #90caf9', borderColor: '#1976d2' },
            '50%': { boxShadow: '0 0 36px #f06292', borderColor: '#f06292' },
            '100%': { boxShadow: '0 0 24px #90caf9', borderColor: '#1976d2' },
          },
        }}
      >
        <CelebrationIcon sx={{ fontSize: 48, color: '#f06292', mb: 1, animation: 'bounce 1.2s infinite' }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1976d2', mb: 1, letterSpacing: 2 }}>
          イベント開始まで
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontFamily: 'monospace', 
              fontWeight: 'bold',
              color: '#d81b60',
              textShadow: '0 2px 8px #fff, 0 0 10px #f8bbd0',
              lineHeight: 1,
              letterSpacing: 2,
              fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.2rem' },
            }}
          >
            {timeObj.hours}
          </Typography>
          <Typography 
            variant="h2" 
            sx={{ 
              fontFamily: 'monospace',
              color: '#d81b60',
              opacity: isSecondEven ? 1 : 0.3,
              transition: 'opacity 0.1s ease-in-out',
              textShadow: '0 2px 8px #fff, 0 0 10px #f8bbd0',
              fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.2rem' },
            }}
          >
            :
          </Typography>
          <Typography 
            variant="h2" 
            sx={{ 
              fontFamily: 'monospace', 
              fontWeight: 'bold',
              color: '#d81b60',
              textShadow: '0 2px 8px #fff, 0 0 10px #f8bbd0',
              lineHeight: 1,
              letterSpacing: 2,
              fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.2rem' },
            }}
          >
            {timeObj.minutes}
          </Typography>
          <Typography 
            variant="h2" 
            sx={{ 
              fontFamily: 'monospace',
              color: '#d81b60',
              opacity: isSecondEven ? 1 : 0.3,
              transition: 'opacity 0.1s ease-in-out',
              textShadow: '0 2px 8px #fff, 0 0 10px #f8bbd0',
              fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.2rem' },
            }}
          >
            :
          </Typography>
          <Typography 
            variant="h2" 
            sx={{ 
              fontFamily: 'monospace', 
              fontWeight: 'bold',
              color: '#d81b60',
              textShadow: '0 2px 8px #fff, 0 0 10px #f8bbd0',
              lineHeight: 1,
              letterSpacing: 2,
              fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.2rem' },
            }}
          >
            {timeObj.seconds}
          </Typography>
        </Box>
        <Typography variant="subtitle1" sx={{ color: '#ad1457', fontWeight: 600, mb: 2 }}>
          ワクワクしながらお待ちください！
        </Typography>
        <Chip 
          label="まもなくスタート！"
          color="primary"
          sx={{ 
            background: 'linear-gradient(135deg, #f06292 0%, #f8bbd0 100%)',
            color: 'white',
            fontWeight: 'bold',
            fontSize: 16,
            boxShadow: '0 0 10px #f8bbd0',
            border: '1.5px solid #d81b60',
            animation: 'pulseStart 1.5s infinite',
            letterSpacing: 1,
          }}
        />
        <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#1976d2', fontWeight: 600 }}>
          イベント開始：{startTime.toLocaleString()}
        </Typography>
      </Box>
    );
  }

  const timeLeft = getSecondsUntil(endTime);

  if (timeLeft === 0) {
    return (
      <Box 
        sx={{ 
          p: 3, 
          background: 'white',
          borderRadius: 3,
          border: '2.5px solid #f06292',
          boxShadow: '0 0 24px #f8bbd0',
        }}
      >
        <Typography 
          variant="h3" 
          sx={{ 
            fontFamily: 'monospace', 
            fontWeight: 'bold',
            color: '#f06292',
            textShadow: '0 0 10px #fff, 0 2px 8px #f8bbd0',
            letterSpacing: 2,
          }}
        >
          終了
        </Typography>
      </Box>
    );
  }

  const timeObj = formatTime(timeLeft);

  return (
    <Box 
      sx={{ 
        p: 3, 
        background: 'white',
        borderRadius: 3,
        border: '2.5px solid #f06292',
        boxShadow: '0 0 24px #f8bbd0',
        animation: timeLeft <= 60 ? 'pulse 1s infinite' : 'none',
        '@keyframes pulse': {
          '0%': {
            boxShadow: '0 0 24px #f8bbd0',
            borderColor: '#f06292',
          },
          '50%': {
            boxShadow: '0 0 36px #d81b60',
            borderColor: '#d81b60',
          },
          '100%': {
            boxShadow: '0 0 24px #f8bbd0',
            borderColor: '#f06292',
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontFamily: 'monospace', 
              fontWeight: 'bold',
              color: timeLeft <= 60 ? '#d81b60' : '#ad1457',
              textShadow: '0 2px 8px #fff, 0 0 10px #f8bbd0',
              lineHeight: 1,
              letterSpacing: 2,
              fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.2rem' },
            }}
          >
            {timeObj.hours}
          </Typography>
          <Typography variant="caption" sx={{ color: '#ad1457', fontWeight: 700, letterSpacing: 1 }}>
            時間
          </Typography>
        </Box>
        <Typography 
          variant="h2" 
          sx={{ 
            fontFamily: 'monospace',
            color: timeLeft <= 60 ? '#d81b60' : '#ad1457',
            opacity: isSecondEven ? 1 : 0.3,
            transition: 'opacity 0.1s ease-in-out',
            textShadow: '0 2px 8px #fff, 0 0 10px #f8bbd0',
            fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.2rem' },
          }}
        >
          :
        </Typography>
        <Box sx={{ textAlign: 'center' }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontFamily: 'monospace', 
              fontWeight: 'bold',
              color: timeLeft <= 60 ? '#d81b60' : '#ad1457',
              textShadow: '0 2px 8px #fff, 0 0 10px #f8bbd0',
              lineHeight: 1,
              letterSpacing: 2,
              fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.2rem' },
            }}
          >
            {timeObj.minutes}
          </Typography>
          <Typography variant="caption" sx={{ color: '#ad1457', fontWeight: 700, letterSpacing: 1 }}>
            分
          </Typography>
        </Box>
        <Typography 
          variant="h2" 
          sx={{ 
            fontFamily: 'monospace',
            color: timeLeft <= 60 ? '#d81b60' : '#ad1457',
            opacity: isSecondEven ? 1 : 0.3,
            transition: 'opacity 0.1s ease-in-out',
            textShadow: '0 2px 8px #fff, 0 0 10px #f8bbd0',
            fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.2rem' },
          }}
        >
          :
        </Typography>
        <Box sx={{ textAlign: 'center' }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontFamily: 'monospace', 
              fontWeight: 'bold',
              color: timeLeft <= 60 ? '#d81b60' : '#ad1457',
              textShadow: '0 2px 8px #fff, 0 0 10px #f8bbd0',
              lineHeight: 1,
              letterSpacing: 2,
              fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.2rem' },
            }}
          >
            {timeObj.seconds}
          </Typography>
          <Typography variant="caption" sx={{ color: '#ad1457', fontWeight: 700, letterSpacing: 1 }}>
            秒
          </Typography>
        </Box>
      </Box>
      {timeLeft <= 600 && timeLeft > 0 && (
        <Chip 
          label="まもなく終了"
          color="warning"
          sx={{ 
            mt: 2,
            background: 'linear-gradient(135deg, #f06292 0%, #f8bbd0 100%)',
            color: 'white',
            fontWeight: 'bold',
            fontSize: 16,
            boxShadow: '0 0 10px #f8bbd0',
            border: '1.5px solid #d81b60',
            animation: 'pulse 1s infinite',
            letterSpacing: 1,
          }}
        />
      )}
      <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#d81b60', fontWeight: 600 }}>
        終了時刻：{endTime.toLocaleString()}
      </Typography>
    </Box>
  );
};

export default CountdownTimer;
